/**
 * Acceptance criteria of one item: a checklist anyone can tick (audited), with what a
 * mission's final report said about each; an editor for Given/When/Then or checklist lines;
 * and paste-in of criteria written as text.
 */
import { ArrowDown, ArrowUp, Bot, ClipboardPaste, ListChecks, Pencil, Plus, Sparkles, Trash2 } from '@orgus/ui/icons';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CRITERION_VERDICT_LABEL, criterionProblem, criterionText, parseCriteria } from '@orgus/protocol';
import type { AcceptanceCriterion, AcceptanceInput, BacklogItem } from '@orgus/protocol';
import { Button, Checkbox, ErrorState, IconButton, Input, Pressable, Segmented, Textarea, Tooltip, cn, toast } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { updateItem } from '../missions/data.ts';
import { checkCriterion } from './data.ts';

type Draft = AcceptanceInput & { key: string };

let seq = 0;
const toDraft = (c: AcceptanceCriterion | AcceptanceInput): Draft => ({ key: `d${++seq}`, id: c.id, format: c.format, text: c.text, given: c.given ?? '', when: c.when ?? '', then: c.then ?? '', done: c.done });

/** Evidence that opens with its own verdict label ("Not checkable:", "Not met:", "Met:"). */
const LABELLED = new RegExp(`^\\s*(${Object.values(CRITERION_VERDICT_LABEL).join('|')})\\s*:`, 'i');

/** The evidence line under a criterion: an unmet one says so once, never "Not met: Not met: ...". */
export function evidenceLine(c: Pick<AcceptanceCriterion, 'done' | 'evidence'>): string {
  if (!c.evidence) return 'Met, by the mission report';
  if (c.done || LABELLED.test(c.evidence)) return c.evidence;
  return `Not met: ${c.evidence}`;
}

/** Evidence clamps to 2 lines; a quiet toggle shows the rest only when it overflows. */
function Evidence({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [long, setLong] = useState(false);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !open) setLong(el.scrollHeight > el.clientHeight + 1);
  }, [text, open]);
  return (
    <span className="flex min-w-0 flex-col items-start gap-1">
      <span ref={ref} title={open ? undefined : text} className={cn('wrap-anywhere', !open && 'line-clamp-2')}>
        {text}
      </span>
      {long && (
        <Pressable aria-expanded={open} onClick={() => setOpen((o) => !o)} className="text-caption text-fg-subtle hover:text-fg-muted">
          {open ? 'Show less' : 'Show more'}
        </Pressable>
      )}
    </span>
  );
}

export function CriteriaEditor({ item, readOnly, onGenerate }: { item: BacklogItem; readOnly?: boolean; onGenerate?: () => void }) {
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [paste, setPaste] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setPaste(null);
    setError(null);
  }, [item.id]);

  const start = () => {
    setDrafts(item.acceptance.length ? item.acceptance.map(toDraft) : [toDraft({ format: 'gwt', text: '' })]);
    setEditing(true);
    setError(null);
  };
  const set = (key: string, patch: Partial<Draft>) => setDrafts((d) => d.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const move = (key: string, by: number) =>
    setDrafts((d) => {
      const i = d.findIndex((x) => x.key === key);
      const j = i + by;
      if (i < 0 || j < 0 || j >= d.length) return d;
      const n = [...d];
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });

  const save = async () => {
    const list = drafts.filter((d) => (d.format === 'gwt' ? `${d.given}${d.when}${d.then}`.trim() : d.text.trim()));
    setBusy(true);
    setError(null);
    try {
      await updateItem(item.id, { acceptance: list.map(({ key: _k, ...c }) => c) });
      setEditing(false);
      toast.success('Acceptance criteria saved', `${list.length} on ${item.key}`);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (c: AcceptanceCriterion, done: boolean) => {
    setPending(c.id);
    try {
      await checkCriterion(item.id, c.id, done);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setPending(null);
    }
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-3">
        <ol className="flex flex-col gap-3">
          {drafts.map((d, i) => (
            <li key={d.key} className="flex flex-col gap-2 rounded-card border border-border-subtle bg-bg p-3">
              <div className="flex items-center gap-2">
                <span className="text-caption font-medium text-fg-subtle num">AC-{i + 1}</span>
                <Segmented
                  label={`Format of AC-${i + 1}`}
                  value={d.format}
                  onChange={(format) => set(d.key, format === 'gwt' && d.format === 'check' ? { format, then: d.then || d.text } : format === 'check' && d.format === 'gwt' ? { format, text: d.text || criterionText({ ...d, text: '' }) } : { format })}
                  options={[
                    { value: 'gwt', label: 'Given, when, then' },
                    { value: 'check', label: 'Checklist line' },
                  ]}
                />
                <span className="ml-auto flex items-center gap-1">
                  <IconButton label="Move up" icon={<ArrowUp />} size="xs" disabled={i === 0} onClick={() => move(d.key, -1)} />
                  <IconButton label="Move down" icon={<ArrowDown />} size="xs" disabled={i === drafts.length - 1} onClick={() => move(d.key, 1)} />
                  <IconButton label={`Remove AC-${i + 1}`} icon={<Trash2 />} size="xs" variant="danger" onClick={() => setDrafts((x) => x.filter((y) => y.key !== d.key))} />
                </span>
              </div>
              {d.format === 'gwt' ? (
                <div className="flex flex-col gap-2">
                  {(
                    [
                      ['given', 'Given', 'a paid order'],
                      ['when', 'When', 'support refunds it'],
                      ['then', 'Then', 'the customer is credited within 1 minute'],
                    ] as const
                  ).map(([k, word, example]) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="w-12 shrink-0 text-ui text-fg-subtle">{word}</span>
                      <Input aria-label={`AC-${i + 1} ${k}`} size="sm" placeholder={example} value={d[k] ?? ''} onChange={(e) => set(d.key, { [k]: e.target.value })} maxLength={1000} wrapperClassName="flex-1" />
                    </div>
                  ))}
                </div>
              ) : (
                <Input aria-label={`AC-${i + 1}`} size="sm" placeholder="A test covers a refund over 30 days" value={d.text} onChange={(e) => set(d.key, { text: e.target.value })} maxLength={1000} />
              )}
              {(() => {
                const p = criterionProblem({ ...d, text: d.text });
                const filled = d.format === 'gwt' ? `${d.given}${d.when}${d.then}`.trim() : d.text.trim();
                return filled && p ? <p className="text-caption text-fg-muted">{p}</p> : null;
              })()}
            </li>
          ))}
        </ol>
        {paste !== null && (
          <div className="flex flex-col gap-2">
            <Textarea
              label="Paste criteria"
              hint='One per line: "Given a, when b, then c" or a checklist line.'
              rows={4}
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setPaste(null)}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const parsed = parseCriteria(paste);
                  setDrafts((d) => [...d.filter((x) => (x.format === 'gwt' ? `${x.given}${x.when}${x.then}`.trim() : x.text.trim())), ...parsed.map(toDraft)]);
                  setPaste(null);
                }}
              >
                Add {parseCriteria(paste).length || ''} criteria
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" icon={<Plus />} onClick={() => setDrafts((d) => [...d, toDraft({ format: 'gwt', text: '' })])}>
            Add criterion
          </Button>
          {paste === null && (
            <Button variant="ghost" size="sm" icon={<ClipboardPaste />} onClick={() => setPaste('')}>
              Paste as text
            </Button>
          )}
          <span className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={busy} onClick={save}>
              Save criteria
            </Button>
          </span>
        </div>
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    );
  }

  if (!item.acceptance.length) {
    return (
      // One quiet line: a mission checks each criterion in its final report, so the gap is worth filling.
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-auto text-ui text-fg-subtle">No criteria yet</span>
        {!readOnly && (
          <>
            <Button variant="ghost" size="sm" icon={<ListChecks />} onClick={start}>
              Write criteria
            </Button>
            {onGenerate && (
              <Button variant="ghost" size="sm" icon={<Sparkles />} onClick={onGenerate}>
                Write with AI
              </Button>
            )}
          </>
        )}
      </div>
    );
  }

  const met = item.acceptance.filter((c) => c.done).length;
  return (
    <div className="group/ac flex flex-col gap-1">
      <ul className="flex flex-col gap-1" aria-label={`Acceptance criteria, ${met} of ${item.acceptance.length} met`}>
        {item.acceptance.map((c, i) => {
          const byMission = c.checkedBy?.startsWith('mission:');
          return (
            <li key={c.id} className="flex items-start gap-3">
              <span className="flex h-5 shrink-0 items-center">
                <Checkbox aria-label={`AC-${i + 1} met`} checked={c.done} disabled={readOnly || pending === c.id} onCheckedChange={(v) => void toggle(c, v)} />
              </span>
              <div className="flex min-w-0 flex-col gap-1">
                {/* The soft body colour: a checklist to scan, not a wall of bright text. Met ones dim further. */}
                <span className={cn('text-ui', c.done ? 'text-fg-subtle' : 'text-fg-muted')}>
                  {c.format === 'gwt' && (c.given || c.when || c.then) ? (
                    <>
                      Given {c.given}, when {c.when}, then {c.then}
                    </>
                  ) : (
                    c.text
                  )}
                </span>
                {(c.evidence || byMission) && (
                  <span className={cn('flex items-start gap-1 text-caption', c.done ? 'text-fg-subtle' : 'text-status-attention')}>
                    {byMission && (
                      <Tooltip content="Checked from the mission's final report">
                        <span className="flex h-4 shrink-0 items-center">
                          <Bot className="size-3 shrink-0" aria-label="From the mission's report" />
                        </span>
                      </Tooltip>
                    )}
                    <Evidence text={evidenceLine(c)} />
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {/* Edit and add show on hover or focus; always on a touch screen. */}
      {!readOnly && (
        <div className="-ml-2 flex items-center gap-1 opacity-0 transition-opacity duration-(--duration-fast) group-hover/ac:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
          <Button variant="ghost" size="sm" icon={<Pencil />} onClick={start}>
            Edit criteria
          </Button>
          {onGenerate && (
            <Button variant="ghost" size="sm" icon={<Sparkles />} onClick={onGenerate}>
              Add with AI
            </Button>
          )}
        </div>
      )}
      {error && <ErrorState variant="inline" error={error} />}
    </div>
  );
}
